<script setup lang="ts">
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import MoneyFormatter from '@apps/main/new-desktop/core/components/formatters/moneyFormatters/MoneyFormatter.vue';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppButton } from '@libs/ui';

import type { ActivityDetailsFooterData } from '../../models';

const browserStore = useBrowserStore();

interface ActivityDetailsFooterProps {
  data: ActivityDetailsFooterData;
}

const props = defineProps<ActivityDetailsFooterProps>();

interface ActivityDetailsFooterEmits {
  (event: 'close'): void;
  (event: 'select-tickets'): void;
}
const emit = defineEmits<ActivityDetailsFooterEmits>();

const { t } = i18n.global;

function handleSelect(): void {
  emit('select-tickets');
}

const headerLabel = computed<string>(() => {
  if (props.data.ticketCount > 0) {
    return `${props.data.ticketCount} ${t(
      'planner.tickets_selected',
      props.data.ticketCount
    )}`;
  }
  return t('planner.no_tickets_selected');
});
</script>

<template>
  <!--TODO: Duplicated component we should remove the old Ticket after release new activity detail page-->
  <footer class="activity-details-footer">
    <div class="payment-summary">
      <div class="total-price">
        <span class="total-price-header">
          {{ headerLabel }}
        </span>
        <MoneyFormatter
          v-if="!props.data.showSameAsTravelers && props.data.totalPrice"
          class="total-price-price"
          :data="{
            price: props.data.totalPrice.amount,
            currency: props.data.totalPrice.currency,
            minPrice: undefined,
          }"
        />
      </div>
    </div>
    <div class="button-wrapper">
      <AppButton
        v-if="browserStore.isLargerThan1199px"
        class="select-activities-button"
        state="enabled"
        size="medium"
        type="modern"
        @click="emit('close')"
      >
        <span>
          {{ $t('planner.close') }}
        </span>
      </AppButton>
      <AppButton
        v-if="props.data.initialTicketsCount > 0"
        class="select-activities-button"
        :state="props.data.hasChanges ? 'enabled' : 'disabled'"
        size="medium"
        type="primary"
        @click="handleSelect()"
      >
        <span>
          {{ $t('planner.update') }}
        </span>
      </AppButton>
      <AppButton
        v-else
        class="select-activities-button"
        :state="props.data.ticketCount > 0 ? 'enabled' : 'disabled'"
        size="medium"
        type="primary"
        @click="handleSelect()"
      >
        <span>
          {{ $t('planner.select') }}
        </span>
      </AppButton>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.activity-details-footer {
  padding: var(--spacing-24);
  display: flex;
  z-index: 1;
  align-items: center;
  justify-content: space-between;
  @include for-less-or-equal-than-900-px {
    padding: var(--spacing-16) var(--spacing-24);
  }
  @include for-more-or-equal-than-1200-px {
    border-radius: 0 0 var(--border-radius-12) var(--border-radius-12);
  }

  .payment-summary {
    margin-right: var(--spacing-24);
    text-align: right;
    display: flex;

    .total-price {
      display: flex;
      flex-direction: column;
      justify-content: center;

      .total-price-header {
        color: var(--color-neutral-70);
        @include font-label-m-light;
      }

      .total-price-price {
        color: var(--color-neutral-70);
        @include font-heading-3;
      }
    }
  }

  .payment-summary {
    text-align: left;
  }
  .button-wrapper {
    display: flex;
    gap: var(--spacing-24);

    .select-activities-button {
      min-width: 125px;
      margin: var(--spacing-2) 0;
    }
  }
}
</style>
